import React, { memo, MouseEvent, useCallback } from 'react';

import InfoIcon from '@material-ui/icons/Info';
import { Box, Typography } from '@material-ui/core';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip
} from '@mui/material';

import { EmaCurationsStatus } from '../../../types/dtos/ema';
import Styles from './EmaCurationsStatusToggle.styles';
import useEmaCurationsStatus from './useEmaCurationsStatus';

type EmaCurationsStatusToggleProps = ReturnType<typeof useEmaCurationsStatus>;

const STATUS_TOOLTIP = (
  <span>
    <strong>DONE:</strong> Curation process is completed
    <br />
    <strong>PROCESSING:</strong> Curation process currently in-progress
  </span>
);

const EmaCurationsStatusToggle = ({
  currentStatus,
  pendingStatus,
  isUpdating,
  selectStatus,
  closeDialog,
  confirmStatusChange
}: EmaCurationsStatusToggleProps) => {
  const classes = Styles();

  const handleStatusChange = useCallback(
    (_: MouseEvent<HTMLElement>, status: EmaCurationsStatus | null) => selectStatus(status),
    [selectStatus]
  );

  return (
    <Box className={classes.container}>
      <Typography className={classes.label}>
        Curation Status
        <Tooltip title={STATUS_TOOLTIP} placement='top' arrow>
          <InfoIcon fontSize='small' className={classes.infoIcon} />
        </Tooltip>
      </Typography>

      <ToggleButtonGroup
        className={classes.root}
        color='primary'
        value={currentStatus}
        exclusive
        disabled={!currentStatus || isUpdating}
        onChange={handleStatusChange}
        aria-label='Curation Status'>
        <ToggleButton value='processing'>Processing</ToggleButton>
        <ToggleButton value='done'>Done</ToggleButton>
      </ToggleButtonGroup>

      <Dialog
        open={pendingStatus !== null}
        onClose={closeDialog}
        aria-labelledby='ema-curation-status-dialog-title'
        aria-describedby='ema-curation-status-dialog-description'>
        <DialogTitle id='ema-curation-status-dialog-title'>Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id='ema-curation-status-dialog-description'>
            Are you sure you want to change the curation status{' '}
            {pendingStatus && `to ${pendingStatus.toUpperCase()}`}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disabled={isUpdating} onClick={closeDialog}>
            Disagree
          </Button>
          <Button disabled={isUpdating} onClick={confirmStatusChange}>
            Agree
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default memo(EmaCurationsStatusToggle);
